import { useTranslation } from 'react-i18next';
import { AlgComponentType, SectionWithSlots } from '@youshift/shared/types';
import { dateToString, returnColor } from '@youshift/shared/utils';
import { useState, useMemo } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { ComponentPrioritySchema } from '@youshift/shared/hooks/mutations';

import ImportanceInput from './ImportanceInput';
import InfoIcon from '../InfoIcon';
import { getConstraintCountByType } from './utils';

interface VirtualSlotPriorityProps {
  sectionsWithSlots: SectionWithSlots[];
  importanceValues: ComponentPrioritySchema;
  setImportanceValues: (
    fn: (prev: ComponentPrioritySchema) => ComponentPrioritySchema,
  ) => void;
}

const computeMode = (values: number[]): number => {
  const frequencyMap = new Map<number, number>();
  let maxCount = 0;
  let mode = 0;

  values.forEach(value => {
    const count = (frequencyMap.get(value) || 0) + 1;
    frequencyMap.set(value, count);
    if (count > maxCount) {
      maxCount = count;
      mode = value;
    }
  });

  return mode;
};

export default function VirtualSlotPriority({
  sectionsWithSlots,
  importanceValues,
  setImportanceValues,
}: VirtualSlotPriorityProps) {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState<Set<number>>(
    new Set(),
  );

  const virtualSlotConstraints = useMemo(
    () =>
      getConstraintCountByType(
        importanceValues,
        AlgComponentType.VIRTUAL_SLOT_NEEDS_MIN,
      ) +
      getConstraintCountByType(
        importanceValues,
        AlgComponentType.VIRTUAL_SLOT_NEEDS_MAX,
      ),
    [importanceValues],
  );

  // Compute initial section values based on mode of virtual slots
  const initialSectionValues = useMemo(() => {
    const values: Record<number, { min: number; max: number }> = {};

    sectionsWithSlots.forEach(section => {
      const virtualSlots = Object.values(section.virtual_slots);
      if (virtualSlots.length > 0) {
        const minValues = virtualSlots.map(
          slot =>
            importanceValues[AlgComponentType.VIRTUAL_SLOT_NEEDS_MIN]?.[
              slot.id_virtual_slot
            ] ?? slot.min_need,
        );
        const maxValues = virtualSlots.map(
          slot =>
            importanceValues[AlgComponentType.VIRTUAL_SLOT_NEEDS_MAX]?.[
              slot.id_virtual_slot
            ] ?? slot.max_need,
        );

        values[section.section.id_section] = {
          min: computeMode(minValues),
          max: computeMode(maxValues),
        };
      }
    });

    return values;
  }, [sectionsWithSlots, importanceValues]);

  const toggleSection = (sectionId: number) => {
    setExpandedSections((prev: Set<number>) => {
      const newSet = new Set<number>(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="font-medium">
          {t(
            'manager.smartass.algorithmSettingsModal.virtualSlotPriority.title',
          )}
        </h3>
        <InfoIcon
          title={t(
            'manager.smartass.algorithmSettingsModal.virtualSlotPriority.title',
          )}
          content={
            <>
              <p className="pt-4">
                {t(
                  'manager.smartass.algorithmSettingsModal.virtualSlotPriority.description',
                )}
              </p>
              <p className="mt-2 font-medium">
                {t('manager.smartass.algorithmSettingsModal.constraintCount', {
                  count: virtualSlotConstraints,
                })}
              </p>
            </>
          }
          className="w-4 h-4"
          iconSize={6}
          size="2xl"
        />
      </div>
      <div className="space-y-2">
        {sectionsWithSlots
          .filter(
            sectionWithSlots => sectionWithSlots.section.assigned_by_smartass,
          )
          .map(sectionWithSlots => {
            const isExpanded = expandedSections.has(
              sectionWithSlots.section.id_section,
            );
            const initialValues =
              initialSectionValues[sectionWithSlots.section.id_section];

            return (
              <div
                key={sectionWithSlots.section.id_section}
                className="border rounded"
              >
                <button
                  onClick={() =>
                    toggleSection(sectionWithSlots.section.id_section)
                  }
                  className="flex items-center justify-between w-full text-left p-2 hover:bg-gray-50"
                  style={{
                    borderLeft: `4px solid ${returnColor(sectionWithSlots.section.color)}`,
                    backgroundColor: returnColor(
                      sectionWithSlots.section.color,
                      50,
                    ),
                    color: returnColor(sectionWithSlots.section.color, 800),
                  }}
                >
                  <div className="grid grid-cols-[1fr,3fr,auto] w-full">
                    {/* Section Name Column */}
                    <div className="flex items-center">
                      <span className="text-sm font-medium">
                        {sectionWithSlots.section.name}
                      </span>
                    </div>

                    {/* Inputs Column */}
                    <div className="flex justify-center">
                      <table className="border-collapse">
                        <tbody>
                          <tr>
                            <span className="text-sm text-gray-600">
                              {t(
                                'manager.smartass.algorithmComponentType.VIRTUAL_SLOT_NEEDS_MIN',
                              )}
                            </span>
                            <td className="pr-2">
                              <ImportanceInput
                                value={initialValues?.min ?? 0}
                                onChange={value => {
                                  // Update all virtual slots in this section
                                  Object.values(
                                    sectionWithSlots.virtual_slots,
                                  ).forEach(slot => {
                                    setImportanceValues(prev => ({
                                      ...prev,
                                      [AlgComponentType.VIRTUAL_SLOT_NEEDS_MIN]:
                                        {
                                          ...prev[
                                            AlgComponentType
                                              .VIRTUAL_SLOT_NEEDS_MIN
                                          ],
                                          [slot.id_virtual_slot]: value,
                                        },
                                    }));
                                  });
                                }}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="pr-2">
                              <span className="text-sm text-gray-600">
                                {t(
                                  'manager.smartass.algorithmComponentType.VIRTUAL_SLOT_NEEDS_MAX',
                                )}
                              </span>
                            </td>
                            <td className="pr-2">
                              <ImportanceInput
                                value={initialValues?.max ?? 0}
                                onChange={value => {
                                  // Update all virtual slots in this section
                                  Object.values(
                                    sectionWithSlots.virtual_slots,
                                  ).forEach(slot => {
                                    setImportanceValues(prev => ({
                                      ...prev,
                                      [AlgComponentType.VIRTUAL_SLOT_NEEDS_MAX]:
                                        {
                                          ...prev[
                                            AlgComponentType
                                              .VIRTUAL_SLOT_NEEDS_MAX
                                          ],
                                          [slot.id_virtual_slot]: value,
                                        },
                                    }));
                                  });
                                }}
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Chevron Column */}
                    <div className="flex items-center justify-end">
                      {isExpanded ? (
                        <ChevronUpIcon className="h-4 w-4" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  </div>
                </button>
                {isExpanded && (
                  <div className="p-2 space-y-2">
                    {Object.values(sectionWithSlots.virtual_slots).map(slot => (
                      <div
                        key={slot.id_virtual_slot}
                        className="grid grid-cols-[1.5fr,2.5fr,1fr] gap-x-4 items-center hover:bg-gray-50 p-2 rounded"
                      >
                        <div className="text-sm text-gray-600">
                          <div className="text-left">
                            {dateToString(slot.start, 'weekday-hour')}
                            <br />
                            {dateToString(slot.end, 'weekday-hour')}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">
                              {t(
                                'manager.smartass.algorithmComponentType.VIRTUAL_SLOT_NEEDS_MIN',
                              )}
                              :
                            </span>
                            <span className="text-sm font-bold">
                              {slot.min_need}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">
                              {t(
                                'manager.smartass.algorithmComponentType.VIRTUAL_SLOT_NEEDS_MAX',
                              )}
                              :
                            </span>
                            <span className="text-sm font-bold">
                              {slot.max_need}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <ImportanceInput
                              value={
                                importanceValues[
                                  AlgComponentType.VIRTUAL_SLOT_NEEDS_MIN
                                ]?.[slot.id_virtual_slot] ?? 0
                              }
                              onChange={value => {
                                setImportanceValues(prev => ({
                                  ...prev,
                                  [AlgComponentType.VIRTUAL_SLOT_NEEDS_MIN]: {
                                    ...prev[
                                      AlgComponentType.VIRTUAL_SLOT_NEEDS_MIN
                                    ],
                                    [slot.id_virtual_slot]: value,
                                  },
                                }));
                              }}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <ImportanceInput
                              value={
                                importanceValues[
                                  AlgComponentType.VIRTUAL_SLOT_NEEDS_MAX
                                ]?.[slot.id_virtual_slot] ?? 0
                              }
                              onChange={value => {
                                setImportanceValues(prev => ({
                                  ...prev,
                                  [AlgComponentType.VIRTUAL_SLOT_NEEDS_MAX]: {
                                    ...prev[
                                      AlgComponentType.VIRTUAL_SLOT_NEEDS_MAX
                                    ],
                                    [slot.id_virtual_slot]: value,
                                  },
                                }));
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}
